import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Select, MenuItem, FormControl, InputLabel,
  IconButton, Button, Chip, Stack,
} from '@mui/material';
import { Close, ShoppingBag, Add, Remove } from '@mui/icons-material';

const CarryBagModal = ({ open, onClose, carryBags = [], onAdd }) => {
  const [selectedBagId, setSelectedBagId] = useState('');
  const [qty, setQty] = useState(1);
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    if (open && carryBags.length > 0) {
      setSelectedBagId(carryBags[0].id);
      setQty(1);
    }
  }, [open, carryBags]);

  const selectedBag = carryBags.find((b) => b.id === selectedBagId);

  const handleAdd = () => {
    if (!selectedBag || qty < 1) return;
    onAdd(selectedBag, qty);
    onClose();
  };

  // Enter adds to cart; ArrowUp/Down adjust quantity (while the size dropdown isn't open)
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (selectOpen) return;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQty((q) => q + 1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQty((q) => Math.max(1, q - 1));
        return;
      }
      if (e.key === 'Enter' && carryBags.length > 0) {
        e.preventDefault();
        handleAdd();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectOpen, carryBags, handleAdd]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <ShoppingBag sx={{ color: '#F59E0B', fontSize: 22 }} />
        <Typography fontWeight={800} fontSize="1rem" color="#1E293B" sx={{ flexGrow: 1 }}>
          Add Carry Bag
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748B' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        {carryBags.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ShoppingBag sx={{ fontSize: 44, color: '#CBD5E1', mb: 1 }} />
            <Typography color="#94A3B8" fontSize="0.85rem" fontWeight={600}>
              No carry bags in stock
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {/* Size selector */}
            <FormControl fullWidth size="small">
              <InputLabel>Select Size</InputLabel>
              <Select
                value={selectedBagId}
                onChange={(e) => setSelectedBagId(e.target.value)}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => setSelectOpen(false)}
                label="Select Size"
                sx={{ borderRadius: '8px' }}
              >
                {carryBags.map((bag) => (
                  <MenuItem key={bag.id} value={bag.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="0.88rem" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
                        {bag.name}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Typography fontSize="0.88rem" fontWeight={800} color="#4F46E5">
                          ₹{bag.retail_price}
                        </Typography>
                        <Chip
                          label={`${bag.stock_at_store} left`}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.62rem', fontWeight: 700,
                            bgcolor: bag.stock_at_store < 10 ? '#FEF2F2' : '#F0FDF4',
                            color: bag.stock_at_store < 10 ? '#EF4444' : '#16A34A',
                          }}
                        />
                      </Stack>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Qty stepper */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography fontWeight={700} color="#64748B" fontSize="0.85rem" sx={{ flexGrow: 1 }}>
                Quantity
              </Typography>
              <Box sx={{
                display: 'flex', alignItems: 'center',
                bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
                borderRadius: '8px', overflow: 'hidden',
              }}>
                <IconButton
                  size="small"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  sx={{ borderRadius: 0, px: 1.25, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <Typography sx={{ px: 2, fontWeight: 800, fontSize: '1rem', color: '#1E293B', minWidth: 40, textAlign: 'center' }}>
                  {qty}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setQty((q) => q + 1)}
                  sx={{ borderRadius: 0, px: 1.25, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Amount preview */}
            {selectedBag && (
              <Box sx={{
                bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
                borderRadius: '8px', px: 2, py: 1.25,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <Typography fontSize="0.82rem" color="#64748B">
                  {qty} × ₹{selectedBag.retail_price}
                </Typography>
                <Typography fontWeight={800} color="#4F46E5" fontSize="1rem">
                  ₹{(parseFloat(selectedBag.retail_price) * qty).toFixed(2)}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      {carryBags.length > 0 && (
        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={onClose} variant="outlined" sx={{
              flex: 1, fontWeight: 700, textTransform: 'none',
              borderRadius: '8px', color: '#64748B', borderColor: '#E2E8F0',
              '&:hover': { bgcolor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd} variant="contained" disableElevation sx={{
              flex: 1, fontWeight: 800, textTransform: 'none', borderRadius: '8px',
              bgcolor: '#F59E0B', color: '#FFF', '&:hover': { bgcolor: '#D97706' },
            }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CarryBagModal;
